import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { ParametersTypes } from 'resources/ParametersTypes'
import PatientsService from 'services/PatientsService'
import { ageFromBirthdate, convertToLocalTime, createMinAndMaxLineChartObject, filterAndSortDates, filterAndSortSameDayFNS, filterAndSortSpecificDates, getDaysArrayByMonth, getDaysBySelectedRange, getHoursArray, getMaxValuesPerHour, getMaxValuesPerTimeUnit, getMinAndMaxValuesPerTimeUnit } from 'utils/HelperFunctions';
import { subMonths, startOfDay, endOfDay, parse } from 'date-fns'
import { AppColors } from 'resources/AppColors';
import moment from 'moment';
import { addToCache } from './cacheReducer';

export const fetchHeartDataIfNeeded = createAsyncThunk(
  'heartStats/fetchHeartDataIfNeeded',
  async ({ patientId, selectedDate, type, name, aggregation }, { getState, dispatch }) => {
    const startDate = moment(selectedDate).subtract(2, 'months').startOf('month').format("YYYY-MM-DDTHH:mm:ss[Z]");
    const endDate = moment(selectedDate).endOf('month').format(("YYYY-MM-DDTHH:mm:ss[Z]"));
    const key = `heartPulse-${type}-${patientId}`;

    const cache = getState().cache.apiResponses[key];
    const selectedMoment = moment(selectedDate);

    if (cache && selectedMoment.isBetween(moment(cache.startDate), moment(cache.endDate), undefined, '[]')) {
      dispatch(updateStateWithCachedData({ type, data: cache.data, status: 'cached' }));
      return cache.data;
    } else {
      const { data, status } = await PatientsService.getObservations(name, patientId, startDate, endDate, aggregation);
      if (status === 200) {
        const existingData = cache ? cache.data : [];
        const combinedData = [...existingData, ...data];
        dispatch(addToCache({ key, data: combinedData, startDate: cache ? cache.startDate : startDate, endDate }));
        return { data: combinedData, type, status };
      }
    }
  }
)

const initialState = {
  lastWeekPatientData: {},
  lastMonthPatientData: {},
  lastTwoMonthsPatientData: {},
  lastTwoMonthsPerDayInMonthPatientData: {},
  lastTwoMonthsPerHourInDayPatientData: {},
  lastYearsPatientData: {},
  perHourChartMinAndMaxFrequencyInWeek: {},
  perDayChartMinAndMaxFrequencyInMonth: {},
  perDayChartIntensityInMonth: {},
  perHourChartMinAndMaxFrequencyInDay: {},
}

const heartStatsSlice = createSlice({
  name: 'heartStats',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchHeartDataIfNeeded.fulfilled, (state, action) => {
      const { type, data, status } = action.payload;
      switch (type) {
        case "HEARTPULSE_DAY_IN_MONTH":
          state.lastTwoMonthsPerDayInMonthPatientData = { data, status };
          break;
        case "HEARTPULSE_HOUR_IN_DAY":
          state.lastTwoMonthsPerHourInDayPatientData = { data, status };
          break;
        default:
          break;
      }
    })
  },
  reducers: {
    updateStateWithCachedData(state, action) {
      const { type, data, status } = action.payload;
      switch (type) {
        case "HEARTPULSE_DAY_IN_MONTH":
          state.lastTwoMonthsPerDayInMonthPatientData = { data, status };
          break;
        case "HEARTPULSE_HOUR_IN_DAY":
          state.lastTwoMonthsPerHourInDayPatientData = { data, status };
          break;
        default:
          break;
      }
    },
    setPatientStatsLastWeek(state, action) {
      return { ...state, lastWeekPatientData: action.payload }
    },
    setPatientStatsLastMonth(state, action) {
      return { ...state, lastMonthPatientData: action.payload }
    },
    setPatientStatsLastTwoMonths(state, action) {
      return { ...state, lastTwoMonthsPatientData: action.payload }
    },
    setPatientStatsLastYearsPerMonth(state, action) {
      return { ...state, lastYearsPatientData: action.payload }
    },
    setPatientStatsLastYears(state, action) {
      return { ...state, lastYearsPatientData: action.payload }
    },

    setChartMinAndMaxFrequencyLastWeek(state, action) {
      const selectedDate = action.payload
      const { data } = state.lastWeekPatientData

      const dateFormat = "H";

      let minValues, maxValues

      try {
        const dataConvertedToLocalTime = convertToLocalTime(data)
        const heartStatsSelectedDaySortedPerHour = filterAndSortDates(dataConvertedToLocalTime, selectedDate, 'day')

        const valuesPerHour = getMinAndMaxValuesPerTimeUnit(getHoursArray(), heartStatsSelectedDaySortedPerHour, dateFormat)

        minValues = Object.values(valuesPerHour).map(value => value.minValue)
        maxValues = Object.values(valuesPerHour).map(value => value.maxValue)

      } catch {
        minValues = []
        maxValues = []
      }

      const labels = getHoursArray().map(hour => hour.length === 1 ? `0${hour}` : hour)

      const chartObj = createMinAndMaxLineChartObject(minValues, maxValues, labels)

      return { ...state, perHourChartMinAndMaxFrequencyInWeek: chartObj }
    },
    setChartMinAndMaxFrequencyinDay(state, action) {
      const selectedDate = action.payload
      const { data } = state.lastTwoMonthsPerHourInDayPatientData

      const dateFormat = "H";

      let minValues, maxValues

      try {
        const dataConvertedToLocalTime = convertToLocalTime(data)
        const heartStatsSelectedDaySortedPerHour = filterAndSortDates(dataConvertedToLocalTime, selectedDate, 'day')

        const valuesPerHour = getMinAndMaxValuesPerTimeUnit(getHoursArray(), heartStatsSelectedDaySortedPerHour, dateFormat)

        minValues = Object.values(valuesPerHour).map(value => value.minValue)
        maxValues = Object.values(valuesPerHour).map(value => value.maxValue)

      } catch {
        minValues = []
        maxValues = []
      }

      const labels = getHoursArray().map(hour => hour.length === 1 ? `0${hour}` : hour)

      const chartObj = createMinAndMaxLineChartObject(minValues, maxValues, labels)

      return { ...state, perHourChartMinAndMaxFrequencyInDay: chartObj }
    },
    setChartMinAndMaxFrequencyInMonth(state, action) {
      const selectedDate = action.payload
      const { data } = state.lastTwoMonthsPerDayInMonthPatientData

      const dateFormat = "DD/MM/YYYY";

      let minValues, maxValues

      try {
        const heartStatsSelectedMonthSortedPerDay = filterAndSortDates(data, selectedDate, 'month')

        const valuesPerDay = getMinAndMaxValuesPerTimeUnit(getDaysArrayByMonth(selectedDate), heartStatsSelectedMonthSortedPerDay, dateFormat)

        minValues = Object.values(valuesPerDay).map(value => value.minValue)
        maxValues = Object.values(valuesPerDay).map(value => value.maxValue)

      } catch {
        minValues = []
        maxValues = []
      }

      const labels = getDaysArrayByMonth(selectedDate)

      const chartObj = createMinAndMaxLineChartObject(minValues, maxValues, labels)

      return { ...state, perDayChartMinAndMaxFrequencyInMonth: chartObj }
    },
    setChartIntensityInMonth(state, action) {
      const { patientAge } = action.payload
      const { data } = state.lastMonthPatientData

      const maximumHR = 220 - patientAge

      let softIntensity = []
      let moderateIntensity = []
      let vigorousIntensity = []

      const firstDay = startOfDay(subMonths(new Date(), 1))
      const lastDay = endOfDay(new Date())

      const calendar = getDaysBySelectedRange(firstDay, lastDay)

      // TODO: Clean up and move functions outside the reducer
      const calendarWithMaxValuesPerHour = calendar.reduce((dayDict, day) => {
        const selectedDayDataSortedPerHour = filterAndSortSameDayFNS(data, parse(day, 'dd/MM/yyyy', new Date()))

        const maxValuesPerHour = getMaxValuesPerHour(
          getHoursArray(),
          selectedDayDataSortedPerHour,
        )

        const intensitiesDict = {
          [day]: {
            softIntensity: [],
            moderateIntensity: [],
            vigorousIntensity: [],
          }
        }

        const populateIntensities = Object.values(maxValuesPerHour).reduce((dayIntensities, hourIntensity) => {
          const softIntensity = hourIntensity.maxValue > maximumHR * .25 && hourIntensity.maxValue < maximumHR * .6
          const moderateIntensity = hourIntensity.maxValue > maximumHR * .6 && hourIntensity.maxValue < maximumHR * .8
          const vigorousIntensity = hourIntensity.maxValue > maximumHR * .8

          if (softIntensity) {
            dayIntensities[day].softIntensity.push(hourIntensity.maxValue)
          }

          if (moderateIntensity) {
            dayIntensities[day].moderateIntensity.push(hourIntensity.maxValue)
          }

          if (vigorousIntensity) {
            dayIntensities[day].vigorousIntensity.push(hourIntensity.maxValue)
          }

          return dayIntensities
        }, intensitiesDict)

        // * Sets the intensities values in hours for all days
        softIntensity = [
          ...softIntensity,
          populateIntensities[day].softIntensity
        ]

        moderateIntensity = [
          ...moderateIntensity,
          populateIntensities[day].moderateIntensity
        ]

        vigorousIntensity = [
          ...vigorousIntensity,
          populateIntensities[day].vigorousIntensity
        ]

        dayDict.push(populateIntensities)

        // * Returns a map with all days and values (not in use for now)
        return dayDict
      }, [])

      const softIntensityBar = softIntensity.map(dayIntensity => dayIntensity.length)

      const moderateIntensityBar = moderateIntensity.map(dayIntensity => dayIntensity.length)

      const vigorousIntensityBar = vigorousIntensity.map(dayIntensity => dayIntensity.length)

      const softIntensityAvgTime = ((softIntensityBar.map(totalHoursInDay => totalHoursInDay / 24)
        .reduce((result, avgTimeInDay) =>
          (result + avgTimeInDay), 0) / softIntensityBar.length) * 100).toFixed(2)

      const moderateIntensityAvgTime = ((moderateIntensityBar.map(totalHoursInDay => totalHoursInDay / 24)
        .reduce((result, avgTimeInDay) =>
          (result + avgTimeInDay), 0) / softIntensityBar.length) * 100).toFixed(2)

      const vigorousIntensityAvgTime = ((vigorousIntensityBar.map(totalHoursInDay => totalHoursInDay / 24)
        .reduce((result, avgTimeInDay) =>
          (result + avgTimeInDay), 0) / softIntensityBar.length) * 100).toFixed(2)

      const labels = getDaysBySelectedRange(firstDay, lastDay)

      const chartObj = {
        data1: {
          label: `Intensitat suau (hrs). Mitjana per dia: ${softIntensityAvgTime}%`,
          data: softIntensityBar,
          backgroundColor: AppColors.GREEN_CHART,
          borderColor: AppColors.GREEN_CHART,
        },
        data2: {
          label: `Intensitat moderada (hrs). Mitjana per dia: ${moderateIntensityAvgTime}%`,
          data: moderateIntensityBar,
          backgroundColor: AppColors.YELLOW_CHART,
          borderColor: AppColors.YELLOW_CHART,
        },
        data3: {
          label: `Intensitat vigorosa (hrs). Mitjana per dia: ${vigorousIntensityAvgTime}%`,
          data: vigorousIntensityBar,
          backgroundColor: AppColors.RED_CHART,
          borderColor: AppColors.RED_CHART,
        },
        labels,
      }

      return { ...state, perDayChartIntensityInMonth: chartObj, maximumHR }
    },
    resetHeartDataState(state, action) {
      return initialState
    }
  },
})

export const {
  setPatientStatsLastWeek,
  setPatientStatsLastMonth,
  setPatientStatsLastTwoMonths,
  setPatientStatsLastYears,
  setChartMinAndMaxFrequencyLastWeek,
  setChartMinAndMaxFrequencyInMonth,
  setChartMinAndMaxFrequencyinDay,
  setChartIntensityInMonth,
  setPatientStatsLastYearsPerMonth,
  updateStateWithCachedData,
  resetHeartDataState
} = heartStatsSlice.actions

export const getHeartStatsLasTwoMonthsPerDay = (patientId, selectedDate) => {
  return async (dispatch, getState) => {
    const { name, type } = ParametersTypes.HEARTPULSE_DAY_IN_MONTH;
    const params = {
      patientId,
      selectedDate,
      type,
      name,
    }
    dispatch(fetchHeartDataIfNeeded(params));

  }
}

export const getHeartStatsLasTwoMonthsPerHour = (patientId, selectedDate) => {
  return async (dispatch, getState) => {

    const { name, type } = ParametersTypes.HEARTPULSE_HOUR_IN_DAY;
    const params = {
      patientId,
      selectedDate,
      type,
      name,
    }
    dispatch(fetchHeartDataIfNeeded(params));
  }
}

export const getHeartStatsLastMonth = (patientId) => {
  return async (dispatch) => {
    const { name, startDate } = ParametersTypes.HEARTPULSE_LAST_MONTH
    const { data, status } = await PatientsService.getObservations(name, patientId, startDate)
    dispatch(setPatientStatsLastMonth({ data, status }))
  }
}

export const changeDateLastPerDayInMonth = (selectedDate) => {
  return (dispatch) => {
    dispatch(setChartMinAndMaxFrequencyInMonth(selectedDate))
  }
}

export const changeDateIntensityChart = () => {
  return (dispatch, getState) => {
    const patientAge = ageFromBirthdate(getState().patient.birthDay)

    dispatch(setChartIntensityInMonth({ patientAge }))
  }
}

export const changeDateperHourInDay = (selectedDate) => {
  return (dispatch) => {
    dispatch(setChartMinAndMaxFrequencyinDay(selectedDate))
  }
}

export default heartStatsSlice.reducer
